import { useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import clsx from "clsx";
import { Pin } from "lucide-react";
import { dayWindow, overlaps, type AirportConfig, type Turn } from "@yb/core";
import type { Board } from "../lib/api";
import { extensionText } from "../lib/extend";
import { airlineColor, counterFunctionLabel, counterFunctionShort, hhmm, readableOn } from "../lib/format";
import { GridLines, LABEL_W, NowLine, TimeHeader, boardWindow, makeScale, packLanes, useAutoScroll } from "./timeline-shared";

export type ResourceKind = "counters" | "belts" | "outbound";

export interface ResRow {
  id: string;
  label: string;
  sub?: string;
  badge?: { text: string; color: string };
  capacity: number;
}

export interface ResItem {
  key: string;
  rowId: string;
  /**
   * Every flight this bar stands for. Back-to-back flights on one desk are drawn as one
   * bar, and moving the bar must move all of them or the block silently splits.
   */
  legIds: string[];
  turnId: string;
  from: number;
  to: number;
  flight: string;
  airline: string;
  detail?: string;
  /** Short use of this position, e.g. PRI / CHK / BAG on a counter. */
  tag?: string;
  pinned: boolean;
  /** Where the bar would end without a planner's extension; the rest is drawn striped. */
  extendedFrom?: number;
  /** Who extended it, by how much and why, for the striped part's tooltip. */
  extension?: string;
}

const LANE_H = 22;

/** A flight's desks after its bar is dragged from one counter row to another. */
export function movedCounters(current: string[], fromRow: string, toRow: string): string[] {
  // A flight with no desk at all sits in the "Not assigned" row, so there is nothing to replace.
  return current.map((c) => (c === fromRow ? toRow : c)).concat(fromRow === "-" ? [toRow] : []);
}

/** Timeline for check-in counters, reclaim belts or outbound belts. */
export function ResourceTimeline({
  board,
  config,
  now,
  ppm,
  kind,
  rows,
  items,
  canEdit,
  selected,
  focusAt,
  onSelect,
  onMove,
  onReject,
  onHover,
  onContext,
  onExtend,
  emptyText,
}: {
  board: Board;
  config: AirportConfig;
  now: number;
  ppm: number;
  kind: ResourceKind;
  rows: ResRow[];
  items: ResItem[];
  canEdit: boolean;
  selected: string | null;
  focusAt: number | null;
  onSelect: (turnId: string | null) => void;
  onMove: (item: ResItem, toRowId: string) => void;
  onReject: (reason: string) => void;
  onHover: (turnId: string | null, x: number, y: number) => void;
  onContext: (turnId: string, x: number, y: number, item: ResItem) => void;
  /**
   * The right edge of a bar dragged to a new end: keep the desks or belt for the flight
   * until then. Dragged back to the usual end, the extension comes off.
   */
  onExtend?: (item: ResItem, until: number) => void;
  emptyText: string;
}) {
  const tz = board.airport.timezone;
  const { from, to } = boardWindow(board.date, tz);
  const scale = makeScale(from, to, ppm);
  const scroller = useRef<HTMLDivElement>(null);
  useAutoScroll(scroller, scale, now, `${kind}|${board.date}|${ppm}`, focusAt);
  // Faded once over on today's board only: a past day faded whole could not be read.
  const fadeDone = now < dayWindow(board.date, tz).to;
  const [drag, setDrag] = useState<{ item: ResItem; rowId: string; ok: boolean; reason: string; x: number; y: number } | null>(null);
  const [resize, setResize] = useState<{ item: ResItem; startX: number; until: number; x: number; y: number } | null>(null);

  const byRow = useMemo(() => {
    const m = new Map<string, Array<ResItem & { lane: number }>>();
    for (const r of rows) m.set(r.id, packLanes(items.filter((i) => i.rowId === r.id)));
    return m;
  }, [rows, items]);

  const unassigned = items.filter((i) => !rows.some((r) => r.id === i.rowId));

  const check = (item: ResItem, rowId: string): { ok: boolean; reason: string } => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return { ok: false, reason: "Unknown" };
    const busy = items.filter((i) => i.rowId === rowId && !i.legIds.some((l) => item.legIds.includes(l)) && overlaps(i.from, i.to, item.from, item.to)).length;
    if (busy >= row.capacity) {
      return { ok: false, reason: row.capacity === 1 ? "Already in use at that time" : `Full: ${row.capacity} flights at once` };
    }
    return { ok: true, reason: busy ? `Shared with ${busy} other flight${busy > 1 ? "s" : ""}` : "Free" };
  };

  const onPointerDown = (e: RPointerEvent, item: ResItem) => {
    if (!canEdit || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    onHover(null, 0, 0);
    setDrag({ item, rowId: item.rowId, ok: true, reason: "", x: e.clientX, y: e.clientY });
  };
  /** Grabbing a bar's right edge stretches it instead of moving it. */
  const onResizeDown = (e: RPointerEvent, item: ResItem) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    onHover(null, 0, 0);
    setResize({ item, startX: e.clientX, until: item.to, x: e.clientX, y: e.clientY });
  };
  const onPointerMove = (e: RPointerEvent) => {
    if (resize) {
      // Five-minute steps, and never earlier than where the flight's own window ends.
      const step = 5 * 60_000;
      const moved = Math.round(((e.clientX - resize.startX) / ppm) * 60_000 / step) * step;
      setResize({ ...resize, until: Math.max(resize.item.extendedFrom ?? resize.item.to, resize.item.to + moved), x: e.clientX, y: e.clientY });
      return;
    }
    if (!drag) return;
    const el = document.elementsFromPoint(e.clientX, e.clientY).find((n) => (n as HTMLElement).dataset?.row !== undefined) as HTMLElement | undefined;
    const rowId = el?.dataset.row ?? drag.rowId;
    const c = rowId === drag.item.rowId ? { ok: true, reason: "" } : check(drag.item, rowId);
    setDrag({ ...drag, rowId, ok: c.ok, reason: c.reason, x: e.clientX, y: e.clientY });
  };
  const onPointerUp = (e: RPointerEvent) => {
    if (resize) {
      if (resize.until !== resize.item.to) onExtend?.(resize.item, resize.until);
      setResize(null);
      return;
    }
    if (!drag) return;
    if (drag.rowId !== drag.item.rowId) {
      if (!drag.ok && !e.shiftKey) onReject(`${rows.find((r) => r.id === drag.rowId)?.label}: ${drag.reason}. Hold Shift to override.`);
      else onMove(drag.item, drag.rowId);
    } else onSelect(drag.item.turnId);
    setDrag(null);
  };

  // The bar is the airline's colour, so the text on it is whichever ink reads on that
  // colour, as the stands board already decides: white on Sunwing's yellow is unreadable.
  const bar = (it: ResItem & { lane: number }) => (
    <div
      key={it.key}
      role="button"
      tabIndex={0}
      onPointerDown={(e) => onPointerDown(e, it)}
      onMouseEnter={(e) => !drag && onHover(it.turnId, e.clientX, e.clientY)}
      onMouseMove={(e) => !drag && onHover(it.turnId, e.clientX, e.clientY)}
      onMouseLeave={() => onHover(null, 0, 0)}
      onContextMenu={(e) => {
        e.preventDefault();
        onHover(null, 0, 0);
        onContext(it.turnId, e.clientX, e.clientY, it);
      }}
      onClick={() => !canEdit && onSelect(it.turnId)}
      className={clsx(
        "absolute flex items-center gap-1 overflow-hidden rounded px-1.5 text-[10.5px] leading-none font-semibold shadow-sm select-none",
        canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        selected === it.turnId && "ring-2 ring-info",
        drag?.item.key === it.key && "opacity-40",
        it.to < now && fadeDone && "opacity-50",
      )}
      style={{
        left: scale.x(it.from),
        width: Math.max(scale.x((resize?.item.key === it.key ? resize.until : null) ?? it.to) - scale.x(it.from), 14),
        top: 3 + it.lane * LANE_H,
        height: LANE_H - 4,
        background: airlineColor(it.airline, config),
        color: readableOn(airlineColor(it.airline, config)),
      }}
    >
      {it.pinned && <Pin className="size-2.5 shrink-0" aria-label="Set by a planner" />}
      {it.tag && <span className="shrink-0 rounded-sm bg-black/25 px-1 py-px text-[9px] tracking-wide">{it.tag}</span>}
      <span className="truncate">{it.flight}</span>
      {it.detail && <span className="truncate font-normal opacity-85">{it.detail}</span>}
      {it.extendedFrom !== undefined && it.extendedFrom < it.to && (
        <div
          className="extend-tail absolute inset-y-0"
          style={{ left: scale.x(it.extendedFrom) - scale.x(it.from), right: 0 }}
          title={it.extension}
          data-extended={kind}
        />
      )}
      {canEdit && onExtend && it.rowId !== "-" && (
        <div
          className="absolute inset-y-0 right-0 z-10 w-1.5 cursor-ew-resize hover:bg-black/20"
          onPointerDown={(e) => onResizeDown(e, it)}
          title="Drag to keep this open for the flight longer"
        />
      )}
    </div>
  );

  return (
    <div ref={scroller} className="scroll-thin relative h-full overflow-auto" onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
      <div style={{ width: scale.width + LABEL_W }} className="relative">
        <TimeHeader scale={scale} now={now} tz={tz} label={`${board.airport.iata} local time`} />
        {rows.length === 0 && <div className="p-8 text-center text-[13px] text-ink-3">{emptyText}</div>}
        {rows.map((r) => {
          const list = byRow.get(r.id) ?? [];
          const lanes = Math.max(1, r.capacity, ...list.map((i) => i.lane + 1));
          const tint = drag && drag.rowId === r.id && drag.rowId !== drag.item.rowId ? (drag.ok ? "bg-ok/12" : "bg-bad/12") : "";
          return (
            <div key={r.id} className={clsx("flex border-b border-line", tint)} style={{ height: lanes * LANE_H + 6 }} data-row={r.id}>
              <div className="sticky left-0 z-40 flex shrink-0 items-center gap-2 border-r border-line bg-panel px-3" style={{ width: LABEL_W }} data-row={r.id}>
                <div className="min-w-0 flex-1 leading-tight">
                  <span className="truncate text-[12.5px] font-semibold">{r.label}</span>
                  {r.sub && lanes > 1 && <div className="truncate text-[10px] text-ink-3">{r.sub}</div>}
                  {r.sub && lanes <= 1 && <span className="ml-1.5 truncate text-[10px] text-ink-3">{r.sub}</span>}
                </div>
                {r.badge && (
                  <span className="rounded px-1 text-[9px] font-bold" style={{ background: r.badge.color, color: readableOn(r.badge.color) }}>
                    {r.badge.text}
                  </span>
                )}
              </div>
              <div className="relative" style={{ width: scale.width }} data-row={r.id}>
                <GridLines scale={scale} />
                {list.map(bar)}
                <NowLine scale={scale} now={now} />
              </div>
            </div>
          );
        })}
        {unassigned.length > 0 && (
          <div className="flex border-b border-line bg-bad/5" style={{ minHeight: LANE_H + 6 }}>
            <div className="sticky left-0 z-40 flex shrink-0 items-center border-r border-line bg-panel px-3" style={{ width: LABEL_W }}>
              <div>
                <div className="text-[12.5px] font-semibold text-bad">Not assigned</div>
                <div className="text-[10px] text-ink-3">{unassigned.length} flights</div>
              </div>
            </div>
            <div className="relative" style={{ width: scale.width, height: packLanes(unassigned).reduce((m, i) => Math.max(m, i.lane + 1), 1) * LANE_H + 6 }}>
              <GridLines scale={scale} />
              {packLanes(unassigned).map(bar)}
              <NowLine scale={scale} now={now} />
            </div>
          </div>
        )}
      </div>
      {resize && resize.until !== resize.item.to && (
        <div className="pointer-events-none fixed z-50 max-w-72 rounded-md bg-warn px-2 py-1 text-[11px] font-medium text-white shadow-lg" style={{ left: resize.x + 14, top: resize.y + 12 }}>
          {resize.until <= (resize.item.extendedFrom ?? resize.item.to)
            ? `Back to ${hhmm(resize.until, tz)}: extension removed`
            : `Until ${hhmm(resize.until, tz)} (+${Math.round((resize.until - (resize.item.extendedFrom ?? resize.item.to)) / 60_000)} min)`}
        </div>
      )}
      {drag && drag.rowId !== drag.item.rowId && (
        <div
          className={clsx("pointer-events-none fixed z-50 max-w-72 rounded-md px-2 py-1 text-[11px] font-medium text-white shadow-lg", drag.ok ? "bg-ok" : "bg-bad")}
          style={{ left: drag.x + 14, top: drag.y + 12 }}
        >
          {rows.find((r) => r.id === drag.rowId)?.label} — {drag.reason}
        </div>
      )}
    </div>
  );
}

/** Build rows and items for one resource view from the board. */
export function resourceView(board: Board, config: AirportConfig, kind: ResourceKind): { rows: ResRow[]; items: ResItem[] } {
  const tz = board.airport.timezone;
  const turnByLeg = new Map<string, { turnId: string; airline: string; flight: string; pax?: number | null; bags?: number | null }>();
  const turnOf = new Map<string, Turn>();
  for (const t of board.turns) for (const l of [t.arr, t.dep]) if (l) turnOf.set(l.id, t);
  /** The striped part of a bar a planner extended, and what its tooltip says. */
  const extendedPart = (legId: string, a: { to: number; extendedFrom?: number }, resource: ResourceKind) => {
    const ext = turnOf.get(legId)?.extensions?.[resource];
    if (a.extendedFrom === undefined || !ext) return {};
    return { extendedFrom: a.extendedFrom, extension: extensionText(resource, ext, a.extendedFrom, tz) };
  };
  for (const t of board.turns) {
    if (t.arr) turnByLeg.set(t.arr.id, { turnId: t.id, airline: t.airline, flight: t.arr.flightNumber, pax: t.arr.pax, bags: t.arr.bags });
    if (t.dep) turnByLeg.set(t.dep.id, { turnId: t.id, airline: t.airline, flight: t.dep.flightNumber, pax: t.dep.pax, bags: t.dep.bags });
  }
  // A cancelled departure's bags coming back are on the reclaim belts like an arrival's,
  // named so nobody looks for an aircraft behind them. Hovering one shows its C1 card.
  for (const c of board.cancelled ?? []) {
    if (c.bags) turnByLeg.set(c.leg.id, { turnId: c.id, airline: c.airline, flight: `${c.leg.flightNumber} cancelled · bags back`, bags: c.leg.bags });
  }
  const loadText = (pax?: number | null, bags?: number | null) =>
    [pax ? `${pax} pax` : "", bags ? `${bags} bags` : ""].filter(Boolean).join(" · ") || undefined;

  if (kind === "counters") {
    // Show booked pax and, once BPSS2 has them, how many are already through security.
    for (const t of board.turns) {
      if (t.dep && t.dep.screened != null) {
        const f = turnByLeg.get(t.dep.id);
        if (f) turnByLeg.set(t.dep.id, { ...f, flight: `${f.flight} · ${t.dep.screened} scr` });
      }
    }
    const rows: ResRow[] = [...config.counters]
      .sort((a, b) => a.number - b.number)
      .map((c) => ({
        id: c.id,
        label: `Counter ${c.number}`,
        badge: c.dedicatedTo ? { text: config.airlines[c.dedicatedTo]?.iata ?? c.dedicatedTo, color: airlineColor(c.dedicatedTo, config) } : undefined,
        sub: [c.dedicatedTo ? "Dedicated" : "Common use", counterFunctionLabel(c.function)].filter(Boolean).join(" · "),
        capacity: 1,
      }));
    const items: ResItem[] = [];
    const dedicated = new Set(config.counters.filter((c) => c.dedicatedTo).map((c) => c.id));
    for (const ca of Object.values(board.plan.counters)) {
      const f = turnByLeg.get(ca.legId);
      if (!f) continue;
      const pinned = !!board.overrides.counters?.[ca.legId];
      const ids = ca.counters.length ? ca.counters : ["-"];
      for (const id of ids) {
        items.push({
          key: `${ca.legId}|${id}`,
          rowId: id,
          legIds: [ca.legId],
          turnId: f.turnId,
          from: ca.from,
          to: ca.to,
          flight: f.flight,
          airline: f.airline,
          detail: loadText(f.pax),
          tag: ca.labels?.[id] ?? (counterFunctionShort(ca.roles?.[id]) || undefined),
          pinned,
          ...extendedPart(ca.legId, ca, "counters"),
        });
      }
    }
    // The same team works an airline's flights one after another on the same desks, so
    // back-to-back flights read as one block rather than a row of separate bars. Gaps
    // shorter than this are closed up; a real break between flights still shows.
    const SAME_TEAM_GAP = 45 * 60_000;
    const merged: ResItem[] = [];
    for (const rowId of new Set(items.map((i) => i.rowId))) {
      const list = items.filter((i) => i.rowId === rowId).sort((a, b) => a.from - b.from);
      for (const it of list) {
        const last = merged[merged.length - 1];
        const sameTeam = last && last.rowId === rowId && last.airline === it.airline;
        if (sameTeam && it.from <= last.to + (dedicated.has(rowId) ? 0 : SAME_TEAM_GAP)) {
          // The block ends where its last flight ends, extended or not.
          if (it.to >= last.to) {
            last.extendedFrom = it.extendedFrom;
            last.extension = it.extension;
          }
          last.to = Math.max(last.to, it.to);
          last.flight = `${last.flight} + ${it.flight}`;
          last.legIds = [...last.legIds, ...it.legIds];
          if (last.tag !== it.tag) last.tag = undefined;
          last.detail = undefined;
          last.pinned = last.pinned || it.pinned;
        } else merged.push({ ...it, legIds: [...it.legIds] });
      }
    }
    return { rows, items: merged };
  }

  const pool = kind === "belts" ? config.belts.map((b) => ({ ...b, active: true, airlines: [] as string[], sectors: [] as string[] })) : (config.outboundBelts ?? []);
  const rows: ResRow[] = pool
    .filter((b) => b.active)
    .map((b) => ({
      id: b.id,
      label: b.name,
      sub: [`${b.capacity} flight${b.capacity > 1 ? "s" : ""} at once`, b.airlines.length ? b.airlines.map((a) => config.airlines[a]?.iata ?? a).join(" ") : "", b.sectors.length ? b.sectors.join("/") : ""]
        .filter(Boolean)
        .join(" · "),
      capacity: b.capacity,
    }));
  const source = kind === "belts" ? board.plan.belts : board.plan.outbound ?? {};
  const pins = kind === "belts" ? board.overrides.belts : board.overrides.outbound;
  const items: ResItem[] = [];
  for (const b of Object.values(source)) {
    const f = turnByLeg.get(b.legId);
    if (!f) continue;
    items.push({
      key: b.legId,
      rowId: b.beltId ?? "-",
      legIds: [b.legId],
      turnId: f.turnId,
      from: b.from,
      to: b.to,
      flight: f.flight,
      airline: f.airline,
      detail: loadText(f.pax, f.bags) ?? `${hhmm(b.from, tz)}–${hhmm(b.to, tz)}`,
      pinned: pins?.[b.legId] !== undefined,
      ...extendedPart(b.legId, b, kind),
    });
  }
  return { rows, items };
}
